/**
 * Publication Status Badge
 * 
 * Shows the publication status of a Priority List.
 * Indicates whether it's published, when, and by whom.
 */

import {
  Globe,
  FileText,
  Clock,
  User,
  ExternalLink,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import type { FocusList } from '../../../types/prioritization/lifecycle';

// ============================================================================
// Props
// ============================================================================

interface PublicationStatusBadgeProps {
  /** The priority list to show status for */
  focusList: FocusList;
  
  /** Whether to show in compact mode */
  compact?: boolean;
  
  /** Whether the current user can publish this list; when false, Publish is disabled */
  canPublish?: boolean;
  
  /** Reason why publish is disabled (e.g. "Not signed in", "Insufficient permissions to publish"); shown in tooltip when canPublish is false */
  publishDisabledReason?: string;
  
  /** When true, show loading state and disable Publish button */
  isPublishing?: boolean;
  
  /** Callback when user clicks to publish */
  onPublish?: () => void;
  
  /** Callback when user clicks to hand off to planning */
  onHandOff?: () => void;
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatDate(date: Date | undefined): string {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelativeTime(date: Date | undefined): string {
  if (!date) return '';
  
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(date);
}

// ============================================================================
// Sub-Components
// ============================================================================

function StatusBadge({ isPublished }: { isPublished: boolean }) {
  if (isPublished) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] rounded-full text-xs font-medium">
        <Globe className="w-3 h-3" />
        Published
      </span>
    );
  }
  
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded-full text-xs font-medium">
      <FileText className="w-3 h-3" />
      Draft
    </span>
  );
}

function HandoffBadge({ handedOff }: { handedOff: boolean }) {
  if (handedOff) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded-full text-xs font-medium">
        <ExternalLink className="w-3 h-3" />
        Handed Off
      </span>
    );
  }
  
  return null;
}

// ============================================================================
// Main Component
// ============================================================================

export function PublicationStatusBadge({
  focusList,
  compact = false,
  canPublish = true,
  publishDisabledReason,
  isPublishing = false,
  onPublish,
  onHandOff,
}: PublicationStatusBadgeProps) {
  const isPublished = focusList.isPublished ?? false;
  const hasChanges = focusList.hasUnacknowledgedChanges ?? false;
  const handedOff = !!focusList.handedOffToPlanningAt;
  const publishable = canPublish && !!onPublish && !isPublishing;
  // The specific reason Publish is off — never a generic "no permission" when we know better.
  const disabledReason = !canPublish
    ? (publishDisabledReason?.trim() || 'You do not have permission to publish this list')
    : undefined;

  // Compact view - just the badges
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <StatusBadge isPublished={isPublished} />
        <HandoffBadge handedOff={handedOff} />
        {hasChanges && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded-full text-xs font-medium">
            <AlertCircle className="w-3 h-3" />
            Changed
          </span>
        )}
      </div>
    );
  }
  
  // Full view
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-[var(--text-pri)]">Publication Status</h3>
        <div className="flex items-center gap-2">
          <StatusBadge isPublished={isPublished} />
          <HandoffBadge handedOff={handedOff} />
        </div>
      </div>
      
      {/* Publication Details */}
      {isPublished && (
        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-3 text-sm">
            <Clock className="w-4 h-4 text-[var(--text-ter)]" />
            <div>
              <span className="text-[var(--text-ter)]">Published: </span>
              <span className="text-[var(--text-pri)]">{formatDate(focusList.publishedAt)}</span>
              <span className="text-[var(--text-ter)] ml-1">
                ({formatRelativeTime(focusList.publishedAt)})
              </span>
            </div>
          </div>
          
          {focusList.publishedBy && (
            <div className="flex items-center gap-3 text-sm">
              <User className="w-4 h-4 text-[var(--text-ter)]" />
              <div>
                <span className="text-[var(--text-ter)]">By: </span>
                <span className="text-[var(--text-pri)]">{focusList.publishedBy}</span>
              </div>
            </div>
          )}
          
          <div className="flex items-center gap-3 text-sm">
            <FileText className="w-4 h-4 text-[var(--text-ter)]" />
            <div>
              <span className="text-[var(--text-ter)]">Version: </span>
              <span className="text-[var(--text-pri)]">{focusList.version || 1}</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Handoff Details */}
      {handedOff && (
        <div className="p-3 bg-[var(--slate-dim)] mb-4">
          <div className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
            <CheckCircle className="w-4 h-4" />
            <span>Handed off to planning</span>
            <span className="text-[var(--text-sec)]">
              {formatRelativeTime(focusList.handedOffToPlanningAt)}
            </span>
          </div>
          {focusList.planningSystemRef && (
            <a 
              href={focusList.planningSystemRef}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] mt-1 ml-6"
            >
              <ExternalLink className="w-3 h-3" />
              View in planning system
            </a>
          )}
        </div>
      )}
      
      {/* Post-Publish Changes Warning */}
      {hasChanges && (
        <div className="p-3 bg-[var(--mustard-dim)] mb-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-[var(--mustard)] flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-[var(--mustard)]">
                Changes since last publication
              </p>
              <p className="text-xs text-[var(--mustard)] mt-1">
                This list has been modified since it was published. 
                {handedOff && ' Downstream planning systems may need to be updated.'}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Actions: app-consistent buttons; Publish disabled when not publishable */}
      <div className="flex gap-2 flex-wrap">
        {!isPublished && onPublish && (
          <button
            type="button"
            onClick={onPublish}
            disabled={!publishable}
            title={disabledReason}
            className="btn btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPublishing ? (
              <>Publishing…</>
            ) : (
              <>
                <Globe className="w-4 h-4" />
                Publish
              </>
            )}
          </button>
        )}
        
        {isPublished && !handedOff && onHandOff && (
          <button
            type="button"
            onClick={onHandOff}
            className="btn btn-secondary flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Hand Off to Planning
          </button>
        )}
        
        {isPublished && hasChanges && onPublish && (
          <button
            type="button"
            onClick={onPublish}
            disabled={!publishable}
            title={disabledReason}
            className="btn btn-secondary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPublishing ? 'Publishing…' : <><Globe className="w-4 h-4" /> Re-Publish</>}
          </button>
        )}
      </div>
      {/* A disabled button's tooltip is easy to miss (and absent on touch) — say why in the open. */}
      {disabledReason && onPublish && (!isPublished || hasChanges) && (
        <p className="mt-2 text-xs text-[var(--text-ter)]" role="note">
          Publish is off: {disabledReason}
        </p>
      )}
    </div>
  );
}

export default PublicationStatusBadge;
